import { Suspense, FC } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { routes } from '@/config/routes';

// Context providers
import { AuthProvider } from '@/context/AuthContext';
import { useAuth } from '@/context/AuthContext';

import ReactQueryProvider from '@/providers/ReactQueryProvider';

import 'react-toastify/dist/ReactToastify.css';
import '@/styles/globals.css';
import '@/styles/global-new.scss';
import { appRoutes } from './routes/index';
import { GlobalContextProvider } from './context/GlobalContext';
import MainLayout from './components/Layouts/Main';
import SuspenseLoading from './components/common/SuspenseLoading';
import SettingsRoutes from './routes/Settings';
import CommandCenterRoutes from './routes/commandCenter';
import MyWorkspaceRoutes from './routes/myWorkspace';
import ApiRoutes from './routes/ApiRoutes';
import EndCustomerRoutes from './routes/ec';
import EndCustomerLayout from './components/Layouts/EndCustomer';
import { UserRole } from './types';
import { InstallerRoutes } from './routes/installer';
import PublicTokenRoutes from './routes/publicTokenRoutes';

const AppRoutesContent = () => {
    const { auth } = useAuth();
    const isEndCustomer = auth.user?.role === UserRole.END_CUSTOMER;

    return (
        <Suspense fallback={<SuspenseLoading />}>
            {isEndCustomer ? (
                <EndCustomerLayout>
                    <Routes>
                        {PublicTokenRoutes}
                        {InstallerRoutes}
                        {EndCustomerRoutes}
                        {/*
                          * The app's catch-all lives in appRoutes, which is only mounted in
                          * the partner branch below. Without one here, a partner URL left in
                          * the address bar (e.g. /leads, right after switching into an
                          * end-customer account) matches nothing and renders the chrome
                          * around an empty body, with no way back.
                          */}
                        <Route path="*" element={<Navigate to={routes.ec.projects} replace />} />
                    </Routes>
                </EndCustomerLayout>
            ) : (
                <MainLayout>
                    <Routes>
                        {PublicTokenRoutes}
                        {InstallerRoutes}
                        {ApiRoutes}
                        {SettingsRoutes}
                        {MyWorkspaceRoutes}
                        {CommandCenterRoutes}
                        {appRoutes}
                        {EndCustomerRoutes}
                    </Routes>
                </MainLayout>
            )}
        </Suspense>
    )
}

// Main App component
const App: FC = () => {
    return (
        <BrowserRouter>
            <ReactQueryProvider>
                <AuthProvider>
                    <GlobalContextProvider>
                        <AppRoutesContent />
                        <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
                    </GlobalContextProvider>
                </AuthProvider>
            </ReactQueryProvider>
        </BrowserRouter>
    );
};

export default App;
